import { useEffect, useState } from "react";
import { MdEdit } from "react-icons/md";
import { Table } from "@merchstores/shared/elements/Table";
import { CTA } from "@merchstores/shared/elements/Cta";

import { Loading } from "@merchstores/shared/components/Loading"; 
import { Badge, BadgeType } from "@merchstores/shared/elements/Badge";
import { formatLocalShortDate, parseIsoDate } from "@merchstores/admin/utils/DateTime";
import { moneyFormat } from "@merchstores/shared/components/Money";

import { IMerchStore } from "@merchstores/admin/components/MerchStore";
import { IMerchStoreOrder, ICombinedOrder, ACTIVE_ORDER_ID } from "@merchstores/admin/components/MerchStoreOrders";

import { listCombinedOrders } from '@merchstores/admin/components/MerchStoreOrders/CombinedOrders';

import { IOrderDetailsView } from "@merchstores/admin/components/MerchStoreOrdersView";

import OrderCalculator from "@merchstores/admin/components/MerchStoreOrders/OrderCalculator";


import "./styles.scss";


export interface IMerchStoreOrderListProps {
  merchStore: IMerchStore;
  isMobile?: boolean;
  setSelectedOrderDetails(orderDetailsView: IOrderDetailsView): void;
  isLoading: boolean;
}

export const MerchStoreOrderList: React.FC<IMerchStoreOrderListProps> = (props: IMerchStoreOrderListProps) => {
  const merchStore = props.merchStore;
  const {setSelectedOrderDetails, isMobile } = props;
  const [ordersData, setOrdersData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [combinedOrdersList, setCombinedOrdersList] = useState(null);

  useEffect(() => {
    listCombinedOrders(merchStore)
      .catch(err => {
        console.error("Error fetching orders:", err);
      })
      .then(combinedOrdersList => 
      {
        setCombinedOrdersList(combinedOrdersList);
        setLoading(false);
      });

  }, [merchStore])
  
  useEffect(() => {
    if (!combinedOrdersList)
    {
      return;
    }
    const storeOrdersData = combinedOrdersList.orders.map(
      (order: ICombinedOrder) => {
        const data: any = {};

        data.name = {
          desktopOnly: false,
          value: (
            <div className="flex items-center ml-20 w-50 md:w-200">
              {order.name}
            </div>
          )
        };

        data.orderStatus = {
          desktopOnly: false,
          value: (
            <Badge type={ order.status.toLowerCase() as BadgeType }>{order.status}</Badge>
          )
        }

        data.creationDate = {
          desktopOnly: true,
          value: <div className='mx-10'>{order.createdAt ? formatLocalShortDate(parseIsoDate(order.createdAt)) : ''}</div>
        };

        const isActiveOrder = order.id === ACTIVE_ORDER_ID;
        const totalPrice = isActiveOrder ? order.totalPrice : OrderCalculator.calculateSummaryTotal(order);

        data.totalPrice = {
          desktopOnly: true,
          value: (
            <div className="text-right">
              { moneyFormat(totalPrice) }
            </div>
          )
        };

        data.editAction = {
          desktopOnly: false,
          value: (
            <div className="flex w-full justify-end pr-15">
              <CTA
                size="small"
                type="mobile-icon"
                onClick={() => {
                  setSelectedOrderDetails({
                    viewType: "combined_order",
                    combinedOrder: order,
                    individualOrder: null
                  });
                }}
              >
                {isMobile ? <MdEdit size="15px" /> : "View/Edit"}
              </CTA>
            </div>
          ),
        };

        return {
          tr: {
            class: "order-list",
            data,
          },
        };
      }
    );
    setOrdersData(storeOrdersData);
  }, [combinedOrdersList]);

  if (loading) 
  {
    return <Loading isLoading={loading as boolean} /> 
  }
  
  return (
    <div className="order-list-container">
      {ordersData.length ? (
        <Table itemsName="orders" name="orders" data={ordersData} displayFooter={isMobile} />
      ) : (
        <div className="bg-merch-background-gray px-3 py-6 lg:p-6 rounded-md flex justify-between items-center">
          <div className="text-13 italic text-merch-dark-gray opacity-50 font-medium">
            Showing 0 of 0 orders
          </div>
        </div>
      )}
    </div>
  );
};
